import React, { useState, useEffect } from 'react';
import { MessageSquare, Users, Bot, Clock, DollarSign, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../services/api';

function StatCard({ icon: Icon, label, value, color, sub }) {
    return (
        <div className="card" style={{ textAlign: 'center' }}>
            <Icon size={22} color={color} style={{ marginBottom: 12 }} />
            <div className="stat-number">{value}</div>
            <div className="stat-label">{label}</div>
            {sub && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>}
        </div>
    );
}

function formatTokens(n) {
    if (!n || n === 0) return '0';
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return Math.round(n / 1000) + 'K';
    return String(n);
}

function formatUSD(v) {
    var n = parseFloat(v) || 0;
    return '$' + n.toFixed(2);
}

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [funcs, setFuncs] = useState([]);
    const [custos, setCustos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [periodo, setPeriodo] = useState('hoje');

    useEffect(function () { loadData(); var i = setInterval(loadData, 30000); return function () { clearInterval(i); }; }, []);
    useEffect(function () { loadCustos(); }, [periodo]);

    var loadData = async function () {
        try {
            var results = await Promise.all([api.get('/api/dashboard/resumo'), api.get('/api/dashboard/funcionarios')]);
            setData(results[0].data);
            setFuncs(results[1].data);
        } catch (err) { console.error(err); }
        setLoading(false);
    };

    var loadCustos = async function () {
        try {
            var dias = periodo === 'hoje' ? 1 : periodo === 'semana' ? 7 : 30;
            var r = await api.get('/api/dashboard/custos?periodo=' + dias);
            setCustos(r.data || []);
        } catch (err) { console.error(err); }
    };

    if (loading) return <p style={{ color: 'var(--text-muted)' }}>Carregando...</p>;
    if (!data) return <p style={{ color: 'var(--red-500)' }}>Erro ao carregar dados</p>;

    var chartData = (data.ultimos_7_dias || []).map(function (d) {
        return {
            data: new Date(d.data).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
            msgs: parseInt(d.msgs) || 0
        };
    });

    var custoTotalHoje = 0;
    var custoTotalPeriodo = 0;
    custos.forEach(function (c) { custoTotalPeriodo += parseFloat(c.custo_total_usd) || 0; });

    var custosHoje = [];
    if (periodo === 'hoje') {
        custoTotalHoje = custoTotalPeriodo;
    }

    var periodoLabel = periodo === 'hoje' ? 'hoje' : periodo === 'semana' ? 'esta semana' : 'este mês';
    var tokensTotalPeriodo = 0;
    custos.forEach(function (c) { tokensTotalPeriodo += parseInt(c.tokens_total) || 0; });

    var btnStyle = function (p) {
        return {
            padding: '6px 14px',
            fontSize: 12,
            fontWeight: 500,
            border: 'none',
            cursor: 'pointer',
            background: periodo === p ? 'rgba(127,119,221,0.2)' : 'transparent',
            color: periodo === p ? 'var(--purple-400)' : 'var(--text-muted)',
            borderRight: p !== 'mes' ? '1px solid var(--border)' : 'none'
        };
    };

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1>Dashboard</h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 4 }}>Visao geral do Synapse</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                        <button style={btnStyle('hoje')} onClick={function () { setPeriodo('hoje'); }}>Hoje</button>
                        <button style={btnStyle('semana')} onClick={function () { setPeriodo('semana'); }}>Semana</button>
                        <button style={btnStyle('mes')} onClick={function () { setPeriodo('mes'); }}>Mês</button>
                    </div>
                    <span className="badge badge-online">
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green-400)', display: 'inline-block' }}></span>
                        Sistema online
                    </span>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 16 }}>
                <StatCard icon={MessageSquare} label={'Mensagens ' + periodoLabel} value={periodo === 'hoje' ? data.hoje.msgs_hoje : custos.reduce(function (a, c) { return a + (parseInt(c.msgs_total) || 0); }, 0)} color="var(--purple-500)" />
                <StatCard icon={Users} label={'Contatos ' + periodoLabel} value={periodo === 'hoje' ? data.hoje.contatos_hoje : '-'} color="var(--green-500)" />
                <StatCard icon={Bot} label="Funcionarios ativos" value={data.totais.total_funcionarios} color="var(--amber-500)" />
                <StatCard icon={Clock} label="Tempo medio" value={data.hoje.tempo_medio_hoje + 'ms'} color="var(--coral-500)" />
                <StatCard icon={AlertTriangle} label="Erros hoje" value={data.hoje.erros_hoje} color="var(--red-500)" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
                <div className="card" style={{ textAlign: 'center', borderColor: 'var(--purple-500)', borderWidth: 1 }}>
                    <DollarSign size={22} color="var(--purple-400)" style={{ marginBottom: 12 }} />
                    <div className="stat-number" style={{ color: 'var(--purple-400)' }}>{formatUSD(custoTotalPeriodo)}</div>
                    <div className="stat-label">{'Custo IA ' + periodoLabel + ' (USD)'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{formatTokens(tokensTotalPeriodo) + ' tokens'}</div>
                </div>
                <div className="card" style={{ textAlign: 'center', borderColor: 'var(--green-400)', borderWidth: 1 }}>
                    <DollarSign size={22} color="var(--green-400)" style={{ marginBottom: 12 }} />
                    <div className="stat-number" style={{ color: 'var(--green-400)' }}>{'R$ ' + (custoTotalPeriodo * 5.7).toFixed(2)}</div>
                    <div className="stat-label">{'Custo IA ' + periodoLabel + ' (BRL)'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{'câmbio ~5.70'}</div>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
                <div className="card">
                    <h3 style={{ fontSize: 16, marginBottom: 20 }}>Mensagens - ultimos 7 dias</h3>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={chartData}>
                            <XAxis dataKey="data" tick={{ fill: 'var(--text-muted)', fontSize: 12 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 12 }} axisLine={false} tickLine={false} />
                            <Tooltip contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 8 }} />
                            <Bar dataKey="msgs" fill="var(--purple-500)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                <div className="card">
                    <h3 style={{ fontSize: 16, marginBottom: 20 }}>Funcionarios ativos</h3>
                    {funcs.length === 0 ? (
                        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Nenhum funcionario ativo</p>
                    ) : (
                        <table>
                            <thead><tr><th>Nome</th><th>Cliente</th><th>Status</th><th>Msgs</th></tr></thead>
                            <tbody>
                                {funcs.map(function (f) {
                                    return (
                                        <tr key={f.id}>
                                            <td style={{ fontWeight: 500 }}>{f.nome}</td>
                                            <td style={{ color: 'var(--text-secondary)' }}>{f.cliente_nome}</td>
                                            <td><span className={f.whatsapp_conectado ? 'badge badge-online' : 'badge badge-offline'}>{f.whatsapp_conectado ? 'Conectado' : 'Offline'}</span></td>
                                            <td>{f.msgs_hoje}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {custos.length > 0 && (
                <div className="card">
                    <h3 style={{ fontSize: 16, marginBottom: 20 }}>{'Custo por funcionário — ' + periodoLabel}</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Cliente</th>
                                <th>Modelo</th>
                                <th style={{ textAlign: 'right' }}>Msgs</th>
                                <th style={{ textAlign: 'right' }}>Tokens</th>
                                <th style={{ textAlign: 'right' }}>Custo USD</th>
                                <th style={{ textAlign: 'right' }}>Custo BRL</th>
                            </tr>
                        </thead>
                        <tbody>
                            {custos.map(function (c, i) {
                                var custoUSD = parseFloat(c.custo_total_usd) || 0;
                                return (
                                    <tr key={i}>
                                        <td style={{ fontWeight: 500 }}>{c.funcionario_nome}</td>
                                        <td style={{ color: 'var(--text-secondary)' }}>{c.cliente_nome}</td>
                                        <td><span className="badge badge-purple" style={{ fontSize: 11 }}>{c.modelo_ia.replace('claude-', '').replace('-20250514', '')}</span></td>
                                        <td style={{ textAlign: 'right' }}>{c.msgs_total}</td>
                                        <td style={{ textAlign: 'right', color: 'var(--text-secondary)' }}>{formatTokens(parseInt(c.tokens_total))}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--purple-400)' }}>{formatUSD(custoUSD)}</td>
                                        <td style={{ textAlign: 'right', fontWeight: 500, color: 'var(--green-400)' }}>{'R$ ' + (custoUSD * 5.7).toFixed(2)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 12 }}>Valores estimados por modelo. Claude Sonnet ~$3/1M input. Câmbio fixo ~5.70</p>
                </div>
            )}
        </div>
    );
}
